/**
 * Announcements — Engage › Announcements (ADR 0029).
 *
 * HR writes a notice (title, Markdown body, optional cover image and attachment), picks who it is
 * for, and publishes it now or at a set instant; it can be pinned, can expire, and can ask each
 * reader to acknowledge it. Employees see a feed of the ones that apply to them — pinned first,
 * then newest — with unread markers and optional reactions.
 *
 * NOT chat broadcasts (`chat.announcement:broadcast`, a message in a channel) and NOT company
 * policies (ADR 0026, versioned documents). An announcement is a one-off notice: editing a live one
 * corrects it in place (the audit trail keeps what it said before) rather than versioning it.
 *
 * AUDIENCE is ADR 0026's four lists, the same schema and the same match (`companyPolicyAppliesTo`):
 * all empty is the whole organisation, otherwise AND across kinds and OR within a kind.
 *
 * STATUS is derived at request time from four instants, never stored: `draft` (no publish time),
 * `scheduled` (publish time in the future), `live`, `expired` (expiry passed), `archived`
 * (withdrawn by HR). Visibility is a fact about instants, so it needs no timezone; the audience is
 * resolved on the reader's own location day like every other "who is where" question (invariant 3).
 *
 * PERMISSIONS. `/announcements/mine*` answers only for the caller (`announcement:read`). Everything
 * else is `announcement:manage`, checked against the whole organisation — like company policies,
 * an announcement is org-wide communication, not something scoped to a population the admin runs.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, orgIdSchema, uuidSchema } from '../schemas/common.js';
import {
  companyPolicyAudienceLabelsSchema,
  companyPolicyAudienceSchema,
} from './company-policy.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** ADR 0026's audience, reused verbatim — one set of targeting rules across Engage and policies. */
export const announcementAudienceSchema = companyPolicyAudienceSchema;
export type AnnouncementAudience = z.infer<typeof announcementAudienceSchema>;
export const announcementAudienceLabelsSchema = companyPolicyAudienceLabelsSchema;

export const announcementStatusSchema = z.enum([
  'draft',
  'scheduled',
  'live',
  'expired',
  'archived',
]);
export type AnnouncementStatus = z.infer<typeof announcementStatusSchema>;

/** A fixed set, matching the chips the composer offers. Null = uncategorised. */
export const ANNOUNCEMENT_CATEGORIES = [
  'policy_update',
  'culture_events',
  'executive',
  'benefits',
] as const;
export const announcementCategorySchema = z.enum(ANNOUNCEMENT_CATEGORIES);
export type AnnouncementCategory = z.infer<typeof announcementCategorySchema>;

/** A fixed set, so counts stay comparable and nobody reacts with arbitrary text. */
export const ANNOUNCEMENT_REACTIONS = [
  'like',
  'celebrate',
  'love',
  'insightful',
  'support',
] as const;
export const announcementReactionSchema = z.enum(ANNOUNCEMENT_REACTIONS);
export type AnnouncementReaction = z.infer<typeof announcementReactionSchema>;

export const announcementReactionCountSchema = z.object({
  reaction: announcementReactionSchema,
  count: z.number().int().positive(),
});

/** File metadata only. Storage keys never leave the server; bytes come from the download routes. */
export const announcementFileMetaSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});

export const announcementFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

/** Reach: the audience as it stands now, and how much of it has read / acknowledged. */
export const announcementReachSchema = z.object({
  audience: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  unread: z.number().int().nonnegative(),
  /** Zero when the announcement does not ask for acknowledgement. */
  acknowledged: z.number().int().nonnegative(),
  pendingAcknowledgement: z.number().int().nonnegative(),
});
export type AnnouncementReach = z.infer<typeof announcementReachSchema>;

export const announcementSummarySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  title: z.string(),
  status: announcementStatusSchema,
  category: announcementCategorySchema.nullable(),
  pinned: z.boolean(),
  requiresAcknowledgement: z.boolean(),
  /** Only meaningful when `requiresAcknowledgement` is set. */
  acknowledgementDeadline: instantSchema.nullable(),
  allowReactions: z.boolean(),
  /** Notify the audience when it goes live. Applies once: nobody is re-notified on an edit. */
  notifyOnPublish: z.boolean(),
  /** Null while a draft. In the future while scheduled. */
  publishAt: instantSchema.nullable(),
  expiresAt: instantSchema.nullable(),
  archivedAt: instantSchema.nullable(),
  /** When the audience was notified; null if not yet (or `notifyOnPublish` is off). */
  notifiedAt: instantSchema.nullable(),
  cover: announcementFileMetaSchema.nullable(),
  attachment: announcementFileMetaSchema.nullable(),
  audience: announcementAudienceSchema,
  reach: announcementReachSchema,
  createdByName: z.string().nullable(),
  publishedByName: z.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AnnouncementSummary = z.infer<typeof announcementSummarySchema>;

export const announcementDetailSchema = announcementSummarySchema.extend({
  bodyMarkdown: z.string(),
  audienceLabels: announcementAudienceLabelsSchema,
});
export type AnnouncementDetail = z.infer<typeof announcementDetailSchema>;

/** One entry of the caller's feed. */
export const myAnnouncementSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  /** The body as plain text, cut to ~280 characters, for the feed and the Home card. */
  excerpt: z.string(),
  category: announcementCategorySchema.nullable(),
  pinned: z.boolean(),
  publishedAt: instantSchema,
  expiresAt: instantSchema.nullable(),
  authorName: z.string().nullable(),
  hasCover: z.boolean(),
  attachment: announcementFileMetaSchema.nullable(),
  requiresAcknowledgement: z.boolean(),
  /** Shown next to the acknowledge button so the reader knows the deadline — never an org-wide
   *  acknowledgement rate, which is `announcement:manage`-only data (see `AnnouncementReach`). */
  acknowledgementDeadline: instantSchema.nullable(),
  allowReactions: z.boolean(),
  /** Null = unread: the marker the feed shows. */
  readAt: instantSchema.nullable(),
  acknowledgedAt: instantSchema.nullable(),
  /** Requires acknowledgement and the caller has not given it. */
  pendingAcknowledgement: z.boolean(),
  myReaction: announcementReactionSchema.nullable(),
  /** Only reactions with a non-zero count; empty when reactions are off. */
  reactions: z.array(announcementReactionCountSchema),
  /** Saved by the caller to find again later — independent of read/acknowledge/react. */
  bookmarked: z.boolean(),
  /** The caller has posted at least one (non-deleted) comment on this announcement. */
  commented: z.boolean(),
});
export type MyAnnouncement = z.infer<typeof myAnnouncementSchema>;

export const myAnnouncementDetailSchema = myAnnouncementSchema.extend({
  bodyMarkdown: z.string(),
  cover: announcementFileMetaSchema.nullable(),
});
export type MyAnnouncementDetail = z.infer<typeof myAnnouncementDetailSchema>;

export const announcementReachRowSchema = z.object({
  personId: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  readAt: instantSchema.nullable(),
  acknowledgedAt: instantSchema.nullable(),
  reaction: announcementReactionSchema.nullable(),
});
export type AnnouncementReachRow = z.infer<typeof announcementReachRowSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * Everything HR edits. A draft may have an empty body; publishing is where completeness is
 * enforced. `expiresAt` must be after the publish time (checked on save and on publish).
 */
export const announcementBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  bodyMarkdown: z.string().max(50_000).default(''),
  category: announcementCategorySchema.nullable().optional(),
  audience: announcementAudienceSchema.default({}),
  pinned: z.boolean().default(false),
  requiresAcknowledgement: z.boolean().default(false),
  /** Only meaningful when `requiresAcknowledgement` is true; ignored otherwise. Nothing currently
   *  enforces it automatically — it is shown to the reader, not yet escalated by a worker. */
  acknowledgementDeadline: instantSchema.nullable().optional(),
  allowReactions: z.boolean().default(true),
  expiresAt: instantSchema.nullable().optional(),
});
export type AnnouncementBody = z.infer<typeof announcementBodySchema>;

export const publishAnnouncementBodySchema = z.object({
  /** Omitted, null or already past = publish now. A future instant schedules it. */
  publishAt: instantSchema.nullable().optional(),
  /** Tell the audience when it goes live (in-app and whatever channels the org has on). */
  notify: z.boolean().default(true),
});

/** Base64 JSON, like policy documents and expense receipts. */
export const uploadAnnouncementFileBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  contentBase64: z.string().min(1),
});

/** Cover images: PNG, JPEG or WebP, 5 MB at most. */
export const ANNOUNCEMENT_COVER_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** Attachments: PDF, images, Office documents and plain text, 10 MB at most. */
export const ANNOUNCEMENT_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

const myListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  unreadOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  category: announcementCategorySchema.optional(),
  bookmarkedOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  /** Only announcements the caller acknowledged, reacted to, or commented on — their own activity
   *  history, never anyone else's (that's the admin-only `reach` view). */
  activityOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

const adminListQuerySchema = z.object({
  status: z.enum(['all', 'draft', 'scheduled', 'live', 'expired', 'archived']).default('all'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Per-status totals over every announcement, not just the page, so the tiles never lie. */
export const announcementStatusCountsSchema = z.object({
  all: z.number().int().nonnegative(),
  draft: z.number().int().nonnegative(),
  scheduled: z.number().int().nonnegative(),
  live: z.number().int().nonnegative(),
  expired: z.number().int().nonnegative(),
  archived: z.number().int().nonnegative(),
});

const reachQuerySchema = z.object({
  status: z.enum(['all', 'read', 'unread', 'acknowledged', 'pending']).default('all'),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const read = routeMeta('announcement:read');
const manage = routeMeta('announcement:manage');
/** A whole file as base64 is no use in a prompt, and a model cannot produce one for an upload. */
const manageNoAi = routeMeta('announcement:manage', { ai: 'hidden' });

const idParam = z.object({ announcementId: uuidSchema });

export const announcementContract = c.router(
  {
    // ---- Employee feed (literal `/mine` paths first: see contract-route-order.test) ----------
    listMyAnnouncements: {
      method: 'GET',
      path: '/mine',
      query: myListQuerySchema,
      responses: {
        200: z.object({
          items: z.array(myAnnouncementSchema),
          total: z.number().int().nonnegative(),
          unreadCount: z.number().int().nonnegative(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Announcements that apply to me — pinned first, then newest — with unread markers',
      metadata: read,
    },
    getMyAnnouncement: {
      method: 'GET',
      path: '/mine/:announcementId',
      pathParams: idParam,
      responses: {
        200: myAnnouncementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Read one announcement (does not mark it read)',
      metadata: read,
    },
    markMyAnnouncementRead: {
      method: 'POST',
      path: '/mine/:announcementId/read',
      pathParams: idParam,
      body: z.object({}).default({}),
      responses: {
        200: myAnnouncementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Mark an announcement read (idempotent)',
      metadata: read,
    },
    acknowledgeMyAnnouncement: {
      method: 'POST',
      path: '/mine/:announcementId/acknowledge',
      pathParams: idParam,
      body: z.object({}).default({}),
      responses: {
        200: myAnnouncementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The announcement does not ask for acknowledgement. */
        409: errorBodySchema,
      },
      summary: 'Acknowledge that I have read an announcement that asks for it',
      metadata: read,
    },
    reactToMyAnnouncement: {
      method: 'PUT',
      path: '/mine/:announcementId/reaction',
      pathParams: idParam,
      body: z.object({ reaction: announcementReactionSchema.nullable() }),
      responses: {
        200: myAnnouncementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Reactions are turned off for this announcement. */
        409: errorBodySchema,
      },
      summary: 'Set (or clear, with null) my reaction to an announcement',
      metadata: read,
    },
    setMyAnnouncementBookmark: {
      method: 'PUT',
      path: '/mine/:announcementId/bookmark',
      pathParams: idParam,
      body: z.object({ bookmarked: z.boolean() }),
      responses: {
        200: myAnnouncementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Save (or remove) an announcement from my bookmarks',
      metadata: read,
    },
    downloadMyAnnouncementCover: {
      method: 'GET',
      path: '/mine/:announcementId/cover',
      pathParams: idParam,
      responses: {
        200: announcementFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "An announcement's cover image",
      metadata: routeMeta('announcement:read', { ai: 'hidden' }),
    },
    downloadMyAnnouncementAttachment: {
      method: 'GET',
      path: '/mine/:announcementId/attachment',
      pathParams: idParam,
      responses: {
        200: announcementFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Download an announcement's attachment",
      metadata: routeMeta('announcement:read', { ai: 'hidden' }),
    },

    // ---- Administration ------------------------------------------------------------------------
    listAnnouncements: {
      method: 'GET',
      path: '/',
      query: adminListQuerySchema,
      responses: {
        200: z.object({
          items: z.array(announcementSummarySchema),
          /** Matching the status filter, across all pages. */
          total: z.number().int().nonnegative(),
          counts: announcementStatusCountsSchema,
          /** Acknowledgements still owed across every live mandatory announcement. */
          pendingAcknowledgements: z.number().int().nonnegative(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Announcements with their status and reach, paged',
      metadata: manage,
    },
    createAnnouncement: {
      method: 'POST',
      path: '/',
      body: announcementBodySchema,
      responses: {
        201: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Start a new announcement as a draft',
      metadata: manage,
    },
    estimateAnnouncementReach: {
      method: 'POST',
      path: '/estimate-reach',
      body: z.object({ audience: announcementAudienceSchema }),
      responses: {
        200: z.object({ count: z.number().int().nonnegative() }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'How many people an audience currently matches, before saving a draft',
      metadata: manage,
    },
    getAnnouncement: {
      method: 'GET',
      path: '/:announcementId',
      pathParams: idParam,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One announcement with its audience and reach',
      metadata: manage,
    },
    updateAnnouncement: {
      method: 'PUT',
      path: '/:announcementId',
      pathParams: idParam,
      body: announcementBodySchema,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Archived: restore it first. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit an announcement (a live one is corrected in place; nobody is re-notified)',
      metadata: manage,
    },
    deleteAnnouncement: {
      method: 'DELETE',
      path: '/:announcementId',
      pathParams: idParam,
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** It has been live: archive it instead, so its read history stays. */
        409: errorBodySchema,
      },
      summary: 'Delete a draft or a scheduled announcement that never went live',
      metadata: manage,
    },
    publishAnnouncement: {
      method: 'POST',
      path: '/:announcementId/publish',
      pathParams: idParam,
      body: publishAnnouncementBodySchema,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already live, expired or archived. */
        409: errorBodySchema,
        /** Empty body, or an expiry not after the publish time. */
        422: errorBodySchema,
      },
      summary: 'Publish now, or schedule (or reschedule) for a later instant',
      metadata: manage,
    },
    unscheduleAnnouncement: {
      method: 'POST',
      path: '/:announcementId/unschedule',
      pathParams: idParam,
      body: z.object({}).default({}),
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not scheduled (a draft, or already live). */
        409: errorBodySchema,
      },
      summary: 'Take a scheduled announcement back to draft before it goes live',
      metadata: manage,
    },
    setAnnouncementPinned: {
      method: 'POST',
      path: '/:announcementId/pin',
      pathParams: idParam,
      body: z.object({ pinned: z.boolean() }),
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Pin an announcement to the top of the feed, or unpin it',
      metadata: manage,
    },
    setAnnouncementArchived: {
      method: 'POST',
      path: '/:announcementId/archive',
      pathParams: idParam,
      body: z.object({ archived: z.boolean() }),
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A draft has nothing to withdraw: delete it. */
        409: errorBodySchema,
      },
      summary: 'Withdraw an announcement from the feed, or restore it',
      metadata: manage,
    },
    uploadAnnouncementCover: {
      method: 'PUT',
      path: '/:announcementId/cover',
      pathParams: idParam,
      body: uploadAnnouncementFileBodySchema,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not PNG/JPEG/WebP, invalid base64, or over 5 MB. */
        422: errorBodySchema,
      },
      summary: 'Set (or replace) the cover image',
      metadata: manageNoAi,
    },
    removeAnnouncementCover: {
      method: 'DELETE',
      path: '/:announcementId/cover',
      pathParams: idParam,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove the cover image',
      metadata: manage,
    },
    downloadAnnouncementCover: {
      method: 'GET',
      path: '/:announcementId/cover',
      pathParams: idParam,
      responses: {
        200: announcementFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The cover image (drafts included)',
      metadata: manageNoAi,
    },
    uploadAnnouncementAttachment: {
      method: 'PUT',
      path: '/:announcementId/attachment',
      pathParams: idParam,
      body: uploadAnnouncementFileBodySchema,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A type not on the list, invalid base64, or over 10 MB. */
        422: errorBodySchema,
      },
      summary: 'Attach (or replace) a file',
      metadata: manageNoAi,
    },
    removeAnnouncementAttachment: {
      method: 'DELETE',
      path: '/:announcementId/attachment',
      pathParams: idParam,
      responses: {
        200: announcementDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove the attachment',
      metadata: manage,
    },
    downloadAnnouncementAttachment: {
      method: 'GET',
      path: '/:announcementId/attachment',
      pathParams: idParam,
      responses: {
        200: announcementFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Download the attachment (drafts included)',
      metadata: manageNoAi,
    },
    getAnnouncementReach: {
      method: 'GET',
      path: '/:announcementId/reach',
      pathParams: idParam,
      query: reachQuerySchema,
      responses: {
        200: z.object({
          reach: announcementReachSchema,
          items: z.array(announcementReachRowSchema),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Who in the audience has read (and acknowledged) an announcement',
      metadata: manage,
    },
  },
  { pathPrefix: '/announcements' },
);
